import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Package, Edit2, X, Save, Check, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { getProducts, createProduct, updateProduct, formatMoney, getApiError } from '../api';
import { useLanguage } from '../i18n';

const CATEGORIES = ['Knygomatai', 'Paslaugos', 'Grąžinimo dėžės', 'Detektoriai', 'Kita'];

const QdCatalog = () => {
  const { t, language: uiLang } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  
  const user = JSON.parse(localStorage.getItem('qd_user') || '{}');
  const canEdit = ['admin', 'superadmin'].includes(user.role) || user.can_edit_products;
  
  useEffect(() => {
    loadProducts();
  }, []);
  
  const loadProducts = async () => {
    try {
      const response = await getProducts();
      setProducts(response.data);
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setLoading(false);
    }
  };
  
  const handleToggleActive = async (product) => {
    if (!canEdit) return;
    
    try {
      await updateProduct(product.id, { active: !product.active });
      const msg = product.active 
        ? (uiLang === 'lt' ? 'Produktas išjungtas' : 'Product deactivated')
        : (uiLang === 'lt' ? 'Produktas įjungtas' : 'Product activated');
      toast.success(msg);
      loadProducts();
    } catch (error) {
      toast.error(getApiError(error));
    }
  };
  
  const filteredProducts = products.filter(product => {
    if (categoryFilter !== 'all' && product.category !== categoryFilter) return false;
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      product.ref?.toLowerCase().includes(searchLower) ||
      product.name?.toLowerCase().includes(searchLower)
    );
  });
  
  // Category translations
  const getCategoryLabel = (cat) => {
    if (uiLang === 'lt') return cat;
    const translations = {
      'Knygomatai': 'Book Kiosks',
      'Paslaugos': 'Services',
      'Grąžinimo dėžės': 'Return Boxes',
      'Detektoriai': 'Detectors',
      'Kita': 'Other'
    };
    return translations[cat] || cat;
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('catalog.title')}</h1>
          <p className="text-gray-500">
            {filteredProducts.length} {uiLang === 'lt' ? 'produktų' : 'products'}
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => { setEditingProduct(null); setShowModal(true); }}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0F7D70] text-white rounded-lg hover:bg-[#0a5f55] transition-colors"
            data-testid="new-product-button"
          >
            <Plus size={20} />
            {t('catalog.newProduct')}
          </button>
        )}
      </div>
      
      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder={t('catalog.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
            />
          </div>
          
          {/* Category filter */}
          <div className="flex gap-2 overflow-x-auto pb-2 lg:pb-0">
            <button
              onClick={() => setCategoryFilter('all')}
              className={`
                px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors
                ${categoryFilter === 'all' 
                  ? 'bg-[#14233F] text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
              `}
            >
              {t('catalog.categories.all')}
            </button>
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`
                  px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors
                  ${categoryFilter === cat 
                    ? 'bg-[#14233F] text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}
                `}
              >
                {getCategoryLabel(cat)}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Products Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F7D70]" />
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
          <Package size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">{t('catalog.noProducts')}</h3>
          <p className="text-gray-500">
            {search 
              ? (uiLang === 'lt' ? 'Pagal jūsų paiešką nieko nerasta' : 'No results found for your search')
              : (uiLang === 'lt' ? 'Pridėkite pirmą produktą' : 'Add your first product')}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('catalog.form.code')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('catalog.form.name')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('catalog.form.category')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('catalog.form.price')}</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                  {canEdit && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredProducts.map((product) => (
                  <tr 
                    key={product.id}
                    className={`hover:bg-gray-50 transition-colors ${!product.active ? 'opacity-50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{product.ref}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-700 line-clamp-2">{product.name}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {getCategoryLabel(product.category)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-medium text-gray-900">
                        {formatMoney(product.price)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {canEdit ? (
                        <button
                          onClick={() => handleToggleActive(product)}
                          className={`
                            inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors
                            ${product.active 
                              ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}
                          `}
                        >
                          {product.active ? <Check size={14} /> : <XCircle size={14} />}
                          {product.active 
                            ? (uiLang === 'lt' ? 'Aktyvus' : 'Active')
                            : (uiLang === 'lt' ? 'Neaktyvus' : 'Inactive')}
                        </button>
                      ) : (
                        <span className={`
                          inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium
                          ${product.active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-500'}
                        `}>
                          {product.active 
                            ? (uiLang === 'lt' ? 'Aktyvus' : 'Active')
                            : (uiLang === 'lt' ? 'Neaktyvus' : 'Inactive')}
                        </span>
                      )}
                    </td>
                    {canEdit && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end">
                          <button
                            onClick={() => { setEditingProduct(product); setShowModal(true); }}
                            className="p-2 text-gray-400 hover:text-[#0F7D70] hover:bg-[#0F7D70]/10 rounded-lg transition-colors"
                          >
                            <Edit2 size={16} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Modal */}
      {showModal && (
        <ProductModal
          product={editingProduct}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            loadProducts();
          }}
        />
      )}
    </div>
  );
};

// Product Modal
const ProductModal = ({ product, onClose, onSaved }) => {
  const { t, language: uiLang } = useLanguage();
  const [form, setForm] = useState({
    ref: product?.ref || '',
    name: product?.name || '',
    category: product?.category || 'Knygomatai',
    price: product?.price || 0
  });
  const [saving, setSaving] = useState(false);
  
  // Category translations
  const getCategoryLabel = (cat) => {
    if (uiLang === 'lt') return cat;
    const translations = {
      'Knygomatai': 'Book Kiosks',
      'Paslaugos': 'Services',
      'Grąžinimo dėžės': 'Return Boxes',
      'Detektoriai': 'Detectors',
      'Kita': 'Other'
    };
    return translations[cat] || cat;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.ref.trim()) {
      toast.error(t('catalog.messages.codeRequired'));
      return;
    }
    
    if (!form.name.trim()) {
      toast.error(t('catalog.messages.nameRequired'));
      return;
    }
    
    setSaving(true);
    try {
      if (product) {
        await updateProduct(product.id, form);
        toast.success(t('catalog.messages.updated'));
      } else {
        await createProduct(form);
        toast.success(t('catalog.messages.created'));
      }
      onSaved();
    } catch (error) {
      toast.error(getApiError(error));
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Package size={20} className="text-[#0F7D70]" />
            {product 
              ? (uiLang === 'lt' ? 'Redaguoti produktą' : 'Edit Product')
              : t('catalog.newProduct')}
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('catalog.form.code')} *
              </label>
              <input
                type="text"
                value={form.ref}
                onChange={(e) => setForm({ ...form, ref: e.target.value.toUpperCase() })}
                placeholder="MAPA101"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('catalog.form.category')}
              </label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{getCategoryLabel(cat)}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('catalog.form.name')} *
            </label>
            <textarea
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              rows={3}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('catalog.form.price')} *
            </label>
            <input
              type="number"
              value={form.price}
              onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
              step="0.01"
              min="0"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F7D70] focus:border-transparent"
              required
            />
          </div>
          
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 bg-[#0F7D70] text-white rounded-lg hover:bg-[#0a5f55] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? t('common.loading') : (
                <>
                  <Save size={18} />
                  {product ? t('common.save') : t('common.create')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QdCatalog;
